import { endOfDay, isAfter, isBefore, isSameDay, startOfDay, subDays } from 'date-fns';
import { is, enUS as en } from 'date-fns/locale';

import { LocaleCode } from '@locale/locale-store';
import { getScopedTranslate, useScopedTranslate } from '@locale/translate';
import { match } from '@utils/match';
import { formatNumber } from '@utils/numbers';
import { unreachable } from '@utils/types';
import { isValidDateString } from '@utils/validators';

import {
  AppliedFilterData,
  FilterComparer,
  SelectableFilterConfigField,
  FilterConfigType,
  FilterConfig,
  CategoryFilterConfig,
  getFilterColumnId,
} from './common';
import { isValidBooleanComparer } from './menus/boolean-menu';
import { getDateDisplay } from './menus/date-menu';
import { isValidNumberComparer } from './menus/number-menu';

import { FilterActions } from '../table-store';

export const valueAsArray = (value: unknown): Array<unknown> => {
  if (value === undefined || value === null) {
    return [];
  }

  if (Array.isArray(value)) {
    return value;
  }

  return Array(value);
};

export const PROGRESS_COMPARERS: Array<FilterComparer> = ['eq', 'gt', 'lt', 'between'];
export const LOCALE: Record<any, object> = { en, is };
export const inlineTypes: Array<FilterConfigType> = [
  'text',
  'paragraph',
  'email',
  'phone_number',
  'progress',
  'issues',
  'boolean',
];

export const inlineComparers: Array<FilterComparer> = ['today', 'yesterday', 'eq'];
export const multiSelectComparers: Array<FilterComparer> = ['none_of', 'any_of', 'contains_multi'];

export const getUsedFieldConfig = (
  config: CategoryFilterConfig | SelectableFilterConfigField,
  fieldId?: string
) => {
  if (config.type === 'category') {
    return config.fields.find((cfg) => cfg.id === fieldId) as
      | CategoryFilterConfig
      | SelectableFilterConfigField;
  }
  return config;
};

export const getDisplayForFilter = (
  filter: AppliedFilterData,
  config: SelectableFilterConfigField | CategoryFilterConfig,
  lang: LocaleCode
): string => {
  const { t } = getScopedTranslate(filterLocale);

  switch (config.type) {
    case 'user':
    case 'multi_select':
    case 'single_select': {
      const valueArray = valueAsArray(filter.value);
      // Get values - either static array or dynamically computed
      const values = typeof config.values === 'function' ? config.values(filter) : config.values;
      const labels = values
        .filter((configValue: any) => {
          return valueArray.includes(configValue.value);
        })
        .map((option: any) => option.label);
      if (labels.length <= 2) {
        return labels.join(', ');
      }
      return t('xSelected', { count: labels.length });
    }

    case 'progress': {
      if (Array.isArray(filter.value)) {
        return `${Number(filter.value[0]) * 100}% - ${Number(filter.value[1]) * 100}%`;
      }
      return `${Number(filter.value) * 100}%`;
    }

    case 'date': {
      return getDateDisplay(filter, lang);
    }

    case 'issues': {
      const valueArray = valueAsArray(filter.value) as string[];
      // Get values - either static array or dynamically computed
      const values = typeof config.values === 'function' ? config.values(filter) : config.values;
      const labels = valueArray.map((value) => {
        return values.find((field: any) => field.value === value.trim())?.label;
      });

      return labels.join(', ');
    }

    case 'boolean':
      return t(filter.value === 'true' ? 'core.yes' : 'core.no');
    case 'number':
    case 'decimal':
      return formatNumber(Number(filter.value));
    case 'text':
    case 'email':
    case 'phone_number':
    case 'paragraph':
    default:
      return String(filter.value);
  }
};

const filterRowValue = (
  filter: AppliedFilterData,
  row: Record<string, unknown>,
  filterTypeMap: Record<string, FilterConfigType>,
  filterActions: FilterActions
) => {
  if (!filter.comparer) return true;

  const key = getFilterColumnId(filter);
  const filterType = filterTypeMap[key];
  if (!filterType) return true; // No config for this filter (e.g. disabled/removed attribute) — skip
  const rowValue = row[key];

  try {
    return match(filterType, {
      user: () => filterUser(rowValue, filter),
      single_select: () => filterSingleSelect(rowValue, filter),
      date: () => filterDate(rowValue, filter),
      datetime: () => filterDate(rowValue, filter),
      progress: () => filterProgress(rowValue, filter),
      text: () => filterText(rowValue, filter),
      paragraph: () => filterText(rowValue, filter),
      email: () => filterText(rowValue, filter),
      phone_number: () => filterText(rowValue, filter),
      number: () => filterNumber(rowValue, filter),
      decimal: () => filterNumber(rowValue, filter),
      multi_select: () => filterMultiSelect(rowValue, filter),
      issues: () => filterIssues(rowValue, filter),
      category: true,
      boolean: () => filterBoolean(rowValue, filter),
      custom: true, // Custom filters handle their own external query logic
      separator: true,
      quick_filters: true, // Quick filters don't filter data directly
      json: () => filterJson(rowValue, filter),
    });
  } catch (e) {
    // We'll return true just to make sure we dont filter by it
    // but then we just remove this filter since its obviously broken
    setTimeout(() => {
      filterActions.removeFilter(filter.id);
    });
    return true;
  }
};

export const filterData = <T extends Record<string, unknown>>(
  config: FilterConfig,
  data: Array<T>,
  appliedFilters: Array<AppliedFilterData>,
  conjunction: 'and' | 'or' = 'and',
  filterActions: FilterActions
): Array<T> => {
  if (appliedFilters.length === 0) {
    return data;
  }

  const filterTypeMap = config.reduce((obj, filterConfig) => {
    if (filterConfig.type === 'category') {
      filterConfig.fields.forEach((field) => {
        obj[field.id] = field.type;
      });
    } else {
      obj[filterConfig.id] = filterConfig.type;
    }
    return obj;
  }, {} as Record<string, FilterConfigType>);

  const filterFn = (row: (typeof data)[number]) => (filter: AppliedFilterData) => {
    try {
      return filterRowValue(filter, row, filterTypeMap, filterActions);
    } catch (e) {
      // If filtering by this filter fails then I THINK it's better to assume
      // the filter matched and incorrectly show the value rather than incorrectly
      // hiding it. -@siggirh
      return true;
    }
  };

  if (conjunction === 'and') {
    return data.filter((row) => {
      return appliedFilters.every(filterFn(row));
    });
  }

  return data.filter((row) => {
    return appliedFilters.some(filterFn(row));
  });
};

export const filterLocale = {
  en: {
    'no-issue': 'No issues',
    error: 'Error',
    warning: 'Warning',
    xSelected: '{count} Selected',
  },
  is: {
    'no-issue': 'Engar athugasemdir',
    error: 'Villa',
    warning: 'Viðvörun',
    xSelected: '{count} Valið',
  },
};

export const useFilterTranslate = () => {
  return useScopedTranslate(filterLocale);
};

type OptionValue = { shortId?: string; value: string };

const filterSingleSelect = (value: unknown, filter: AppliedFilterData): boolean => {
  /**
   * I expect we can receive values that are typically one of:
   * 1. Empty (null)
   * 2. string
   * 3. {shortId?: string, value: string}
   */
  const identifier: string | undefined =
    typeof value === 'object' && value
      ? (value as OptionValue).shortId ?? (value as OptionValue).value
      : (value as string);

  switch (filter.comparer) {
    case 'is':
      if (!value) return false;
      return identifier === filter.value;

    case 'not_is':
      if (!value) return true;
      return identifier !== filter.value;

    case 'any_of':
      if (!Array.isArray(filter.value)) return true;
      if (!value) return false;
      return filter.value.includes(identifier);

    case 'none_of':
      if (!Array.isArray(filter.value)) return true;
      if (!value) return true;
      return !filter.value.includes(identifier);

    default:
      return true;
  }
};

const filterMultiSelect = (value: unknown, filter: AppliedFilterData): boolean => {
  /**
   * MultiSelect values I expect can be be one of (even though 1 and 3 probably shouldnt):
   * 1. null
   * 2. Empty array
   * 3. Array of string values [string, ...]
   * 4. Array of object values [{shortId?: string, value: string}, ...]
   */

  const items = Array.isArray(value) ? value : [];
  const isObjectArray = items.length > 0 && typeof items[0] === 'object';
  const identifiers = isObjectArray ? items.map((v: OptionValue) => v.shortId ?? v.value) : items;

  switch (filter.comparer) {
    case 'is': {
      if (items.length === 0) return false;
      return identifiers.some((id: string) => id === filter.value);
    }

    case 'not_is': {
      if (items.length === 0) return true;
      return !identifiers.some((id: string) => id === filter.value);
    }

    case 'any_of': {
      if (items.length === 0) return false;
      return identifiers.some((id: string) => filter.value.includes(id));
    }

    case 'none_of': {
      if (items.length === 0) return true;
      return !identifiers.some((id: string) => filter.value.includes(id));
    }

    default:
      return true;
  }
};

const filterUser = (value: unknown, filter: AppliedFilterData): boolean => {
  /**
   * User value should be one of:
   * 1. Empty (null)
   * 2. {shortId: string}
   */
  switch (filter.comparer) {
    case 'is':
      if (!value) return false;
      if (typeof value === 'object' && 'shortId' in value) {
        return value.shortId === filter.value;
      }
      return false;

    case 'not_is':
      if (!value) return true;
      if (typeof value === 'object' && 'shortId' in value) {
        return value.shortId !== filter.value;
      }
      return true;

    case 'any_of':
      if (!Array.isArray(filter.value)) return true;
      if (!value) return false;
      if (typeof value === 'object' && 'shortId' in value) {
        return filter.value.includes(value.shortId as string);
      }
      return true;

    case 'none_of':
      if (!Array.isArray(filter.value)) return true;
      if (!value) return true;
      if (typeof value === 'object' && 'shortId' in value) {
        return !filter.value.includes(value.shortId as string);
      }
      return true;

    default:
      return true;
  }
};

const filterDate = (value: unknown, filter: AppliedFilterData): boolean => {
  /**
   * Date value should be one of:
   * 1. Empty (null)
   * 2. UTC string
   */
  switch (filter.comparer) {
    case 'today': {
      const rowDate = new Date(value as string);
      const today = new Date();
      return isSameDay(rowDate, today);
    }

    case 'yesterday': {
      const rowDate = new Date(value as string);
      const yesterday = subDays(new Date(), 1);
      return isSameDay(rowDate, yesterday);
    }

    case 'after': {
      if (!isValidDateString(value as string)) {
        return false;
      }
      const rowDate = new Date(value as string);
      const filterDate = new Date(filter.value as string);
      return isAfter(rowDate, endOfDay(filterDate));
    }

    case 'before': {
      if (!isValidDateString(value as string)) {
        return false;
      }
      const rowDate = new Date(value as string);
      const filterDate = new Date(filter.value as string);
      return isBefore(rowDate, startOfDay(filterDate));
    }
    case 'on': {
      const rowDate = new Date(value as string);
      const filterDate = new Date(filter.value as string);
      return isSameDay(rowDate, filterDate);
    }

    case 'eq': {
      if (!isValidDateString(value as string)) {
        return false;
      }
      const today = new Date();
      const rowDate = new Date(value as string);
      const filterDate = subDays(today, Number(filter.value));
      return isSameDay(rowDate, filterDate);
    }

    case 'lt': {
      if (!isValidDateString(value as string)) {
        return false;
      }
      const today = new Date();
      const rowDate = new Date(value as string);
      const filterDate = subDays(today, Number(filter.value));
      return isAfter(rowDate, filterDate);
    }

    case 'gt': {
      if (!isValidDateString(value as string)) {
        return false;
      }
      const today = new Date();
      const rowDate = new Date(value as string);
      const filterDate = subDays(today, Number(filter.value));
      return isBefore(rowDate, filterDate);
    }

    default:
      return true;
  }
};

const filterProgress = (value: unknown, filter: AppliedFilterData): boolean => {
  /**
   * Progress value should be one of:
   * 1. Empty (null)
   * 2. Number
   */
  switch (filter.comparer) {
    case 'is': {
      return value === Number(filter.value);
    }

    case 'not_is': {
      return value !== Number(filter.value);
    }

    case 'gt': {
      if (typeof value !== 'number') return false;
      return value > Number(filter.value);
    }

    case 'lt': {
      if (typeof value !== 'number') return false;
      return value < Number(filter.value);
    }

    case 'between': {
      if (typeof value !== 'number') return false;
      if (!Array.isArray(filter.value)) return true;
      const [low, high] = filter.value.map(Number);

      return value >= low && value <= high;
    }

    default:
      return true;
  }
};

const filterText = (value: unknown, filter: AppliedFilterData): boolean => {
  /**
   * Text values should be one of:
   * 1. Empty (null)
   * 2. String
   */
  const stringRowValue = String(value).toLowerCase();
  const stringFilterValue = String(filter.value).toLowerCase();

  switch (filter.comparer) {
    case 'is': {
      if (!value) return false;
      return stringRowValue === stringFilterValue;
    }

    case 'not_is': {
      if (!value) return true;
      return stringRowValue !== stringFilterValue;
    }

    case 'contains': {
      if (!value) return false;
      return stringRowValue.includes(stringFilterValue);
    }

    case 'not_contains': {
      if (!value) return true;
      return !stringRowValue.includes(stringFilterValue);
    }

    case 'starts': {
      if (!value) return false;
      return stringRowValue.startsWith(stringFilterValue);
    }

    case 'not_starts': {
      if (!value) return true;
      return !stringRowValue.startsWith(stringFilterValue);
    }

    default:
      return true;
  }
};

const filterJson = (value: unknown, filter: AppliedFilterData): boolean => {
  const jsonRowValue = (value ?? {}) as object;
  const jsonFilterValue = (filter.value ?? '') as string;

  try {
    const isStringInObject = JSON.stringify(jsonRowValue)
      .toLowerCase()
      .includes(String(jsonFilterValue).toLowerCase());
    switch (filter.comparer) {
      case 'contains': {
        return isStringInObject;
      }

      case 'not_contains': {
        return !isStringInObject;
      }

      default:
        return true;
    }
  } catch {
    return false;
  }
};

const filterIssues = (value: unknown, filter: AppliedFilterData): boolean => {
  const { errors, warnings } = value as { errors: []; warnings: [] };
  const filterValues = valueAsArray(filter.value);

  return filterValues.some((filterValue) => {
    switch (filterValue) {
      case 'error': {
        return Array.isArray(errors) && errors.length > 0;
      }
      case 'warning': {
        return Array.isArray(warnings) && warnings.length > 0;
      }
      case 'no-issue': {
        return (
          Array.isArray(errors) &&
          errors.length === 0 &&
          Array.isArray(warnings) &&
          warnings.length === 0
        );
      }
      default:
        return true;
    }
  });
};

const filterNumber = (value: unknown, filter: AppliedFilterData): boolean => {
  /**
   * Number values should be one of:
   * 1. Empty (null)
   * 2. Numbe
   */
  const rowValue = Number(value);
  const filterValue = typeof filter.value === 'number' ? filter.value : Number(filter.value);

  if (!isValidNumberComparer(filter.comparer)) {
    return true;
  }

  switch (filter.comparer) {
    case 'is': {
      if (value === undefined || isNaN(rowValue)) return false;
      return rowValue === filterValue;
    }

    case 'not_is': {
      if (value === undefined || isNaN(rowValue)) return true;
      return rowValue !== filterValue;
    }

    case 'gt': {
      if (value === undefined || isNaN(rowValue)) return false;
      return rowValue > filterValue;
    }

    case 'lt': {
      if (value === undefined || isNaN(rowValue)) return false;
      return rowValue < filterValue;
    }

    default:
      return unreachable(filter.comparer, true);
  }
};

const filterBoolean = (value: unknown, filter: AppliedFilterData): boolean => {
  /**
   * Boolean values should be one of:
   * 1. Empty/null
   * 2. true
   * 3. false
   */
  const rowValue = value === undefined ? value : String(value);
  const filterValue = filter.value;

  if (!isValidBooleanComparer(filter.comparer)) {
    return true;
  }

  switch (filter.comparer) {
    case 'is': {
      if (rowValue === undefined) return false;
      return rowValue === filterValue;
    }

    default:
      return unreachable(filter.comparer, true);
  }
};
